/* Global TD behaviors, loaded from snippets/scripts.liquid (TD CHANGE). */

/* Accent underline reveal: rendered by snippets/td-accent-richtext.liquid for
   [accent-underlined] tokens. Flips data-td-revealed once the word is in view so the
   CSS clip-path transition sweeps the underline in left to right. Above-the-fold words
   are checked on connect so they animate on the initial landing instead of remaining
   in the pre-upgrade, fully-drawn state. Without JS the element never upgrades and the
   underline renders fully drawn (see assets/td-base.css). */
if (!customElements.get('td-accent-underlined')) {
  class TDAccentUnderlined extends HTMLElement {
    // The theme editor's section morphing can reset the attribute on a live element;
    // watching it lets the reveal re-arm and replay instead of staying hidden.
    static observedAttributes = ['data-td-revealed'];

    static revealDelay = 100;

    connectedCallback() {
      this.arm();
    }

    disconnectedCallback() {
      this.disarm();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (newValue === 'false' && this.isConnected) {
        this.arm();
      }
    }

    arm() {
      if (this.dataset.tdRevealed === 'true' || this.observer) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) {
            return;
          }

          this.disarm();
          this.scheduleReveal();
        },
        { threshold: 0.1 }
      );
      this.observer.observe(this);
      this.revealIfVisible();
    }

    revealIfVisible() {
      const bounds = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      if (bounds.bottom > 0 && bounds.top < viewportHeight) {
        this.disarm();
        this.scheduleReveal();
      }
    }

    scheduleReveal() {
      if (this.dataset.tdRevealed === 'true' || this.revealTimeoutId) {
        return;
      }

      this.revealTimeoutId = setTimeout(() => {
        this.revealTimeoutId = null;

        if (this.isConnected && this.dataset.tdRevealed !== 'true') {
          this.dataset.tdRevealed = 'true';
        }
      }, TDAccentUnderlined.revealDelay);
    }

    disarm() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.revealTimeoutId) {
        clearTimeout(this.revealTimeoutId);
        this.revealTimeoutId = null;
      }

    }
  }

  customElements.define('td-accent-underlined', TDAccentUnderlined);
}

/* At >=990px base.css makes .page-wrapper the scroll container, so scroll never reaches
   window and third-party lazy-loaders (Videowise) never fire on desktop. Relay it. */
if (!customElements.get('td-scroll-bridge')) {
  class TDScrollBridge extends HTMLElement {
    connectedCallback() {
      // Theme scroll container; class query matches scroll-container.js (not editable markup).
      this.wrapper = document.querySelector('.page-wrapper');
      if (!this.wrapper) return;

      this.mq = matchMedia('(min-width: 990px)');
      this.relay = this.relay.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.wrapper.addEventListener('scroll', this.handleScroll, { passive: true });
      if (this.mq.matches) {
        this.primers = [0, 600, 1800, 4000].map((delay) => setTimeout(this.relay, delay));
      }
    }

    disconnectedCallback() {
      this.wrapper?.removeEventListener('scroll', this.handleScroll);
      this.primers?.forEach(clearTimeout);
      if (this.rafId != null) cancelAnimationFrame(this.rafId);
    }

    handleScroll() {
      if (!this.mq.matches || this.queued) return;
      this.queued = true;
      this.rafId = requestAnimationFrame(this.relay);
    }

    relay() {
      this.rafId = null;
      this.queued = false;
      window.dispatchEvent(new Event('scroll'));
      document.dispatchEvent(new Event('scroll'));
    }
  }

  customElements.define('td-scroll-bridge', TDScrollBridge);
  document.body.append(document.createElement('td-scroll-bridge'));
}
